import React, { useState } from "react";
import { Check, Plus, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { COUNTRIES } from "@/lib/countries";
import { QUICK_MARKETS } from "@/lib/profileOptions";

// Searchable multi-select for source markets. Quick chips toggle; any market can be added from the
// full country list (lib/countries.js). Legacy/added markets outside the quick set show as removable
// chips. Deduped across quick chips, search additions and already-stored values. Display-only names
// (FR) — no backend validation, no migration.
export const MarketSelect = ({ value = [], onChange, canManage = true, testid = "market-select" }) => {
  const [open, setOpen] = useState(false);

  const has = (name) => value.includes(name);
  const add = (name) => { if (!has(name)) onChange([...value, name]); };
  const remove = (name) => onChange(value.filter((v) => v !== name));
  const toggle = (name) => (has(name) ? remove(name) : add(name));

  // Selected markets not part of the quick list (legacy values + search additions).
  const extraSelected = value.filter((v) => !QUICK_MARKETS.includes(v));
  // Addable = full country list minus everything already selected (prevents duplicates).
  const addable = COUNTRIES.filter((c) => !has(c.name));

  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="flex flex-wrap gap-2">
        {QUICK_MARKETS.map((m) => (
          <button
            key={m}
            type="button"
            data-testid={`profile-market-${m}`}
            onClick={() => canManage && toggle(m)}
            className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-all ${
              has(m)
                ? "border-[#1E3D2B] bg-[#1E3D2B] text-white"
                : "border-slate-200 bg-white text-slate-600 hover:border-[#CBD5E1]"
            }`}
          >
            {has(m) && <Check size={12} className="mr-1 inline" />}
            {m}
          </button>
        ))}

        {canManage && (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="market-add-trigger"
                className="flex items-center gap-1 rounded-full border border-dashed border-[#3E7256] px-3.5 py-1.5 text-xs font-semibold text-[#3E7256] transition-colors hover:bg-[#ECF3EC]"
              >
                <Plus size={13} /> Ajouter un marché
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="start">
              <Command>
                <CommandInput placeholder="Rechercher un pays…" data-testid="market-add-search" />
                <CommandList>
                  <CommandEmpty>Aucun pays trouvé.</CommandEmpty>
                  <CommandGroup>
                    {addable.map((c) => (
                      <CommandItem
                        key={c.name}
                        value={c.name}
                        data-testid={`market-add-option-${c.name}`}
                        onSelect={() => { add(c.name); setOpen(false); }}
                        className="flex items-center gap-2"
                      >
                        <span className="text-base">{c.flag}</span>
                        <span className="flex-1">{c.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {extraSelected.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="market-extra-selected">
          {extraSelected.map((m) => (
            <span
              key={m}
              data-testid={`market-chip-${m}`}
              className="flex items-center gap-1.5 rounded-full border border-[#3E7256] bg-[#ECF3EC] px-3 py-1.5 text-xs font-medium text-[#1E3D2B]"
            >
              {m}
              {canManage && (
                <button
                  type="button"
                  data-testid={`market-remove-${m}`}
                  onClick={() => remove(m)}
                  className="text-[#3E7256] hover:text-[#1E3D2B]"
                  aria-label={`Retirer ${m}`}
                >
                  <X size={13} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
